import React, { useCallback } from 'react';
import { Modal, Text, Button, Stack, Group, Alert } from '@mantine/core';
import { IconPlayerPause, IconX, IconAlertTriangle } from '@tabler/icons-react';
import { BusinessListItemDto } from '@/domain/entities/Business';
import { useDeactivateBusinessMutation } from '@/application/queries/businessQueries';
import { useAuth } from '../../../hooks/useAuth';

export interface DeactivateBusinessModalProps {
  isOpen: boolean;
  business: BusinessListItemDto | null;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * DeactivateBusinessModal - Confirmation modal for deactivating a business
 * Sprint 045: Renamed from DeleteBusinessModal — businesses are deactivated, not deleted.
 *
 * Features:
 * - Confirmation dialog with business name
 * - Warning about cascade effects (mentions become inactive)
 * - Success/error notifications via mutation hook
 * - Loading state during deactivation
 * - Admin-only operation
 */
export const DeactivateBusinessModal: React.FC<DeactivateBusinessModalProps> = ({
  isOpen,
  business,
  onClose,
  onSuccess
}): React.ReactElement => {
  const { user } = useAuth();
  const deactivateBusinessMutation = useDeactivateBusinessMutation();

  const handleDeactivate = useCallback(async (): Promise<void> => {
    if (!business) {
      return;
    }

    try {
      await deactivateBusinessMutation.mutateAsync({
        id: business.id,
        userRole: user?.role ?? 'BusinessOwner'
      });

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Deactivate business failed:', error);
    }
  }, [business, deactivateBusinessMutation, user, onClose, onSuccess]);

  const handleCancel = useCallback((): void => {
    onClose();
  }, [onClose]);

  if (!business) {
    return <></>;
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Deactivate Business"
      size="md"
      centered
    >
      <Stack gap="md">
        <Alert icon={<IconAlertTriangle />} title="Warning" color="orange" variant="light">
          Deactivating this business will:
        </Alert>

        <Stack gap="xs" pl="md">
          <Text size="sm">• All {business.mentionCount} associated mention{business.mentionCount !== 1 ? 's' : ''} will be marked inactive</Text>
          <Text size="sm">• The business will no longer match new Reddit mentions</Text>
          <Text size="sm">• This action can be reversed by reactivating the business</Text>
        </Stack>

        <Text size="sm" mt="md">
          Are you sure you want to deactivate <Text span fw={700}>{business.name}</Text>?
        </Text>

        <Stack gap={4} mt="xs" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: '4px' }}>
          <Text size="sm"><Text span fw={600}>Name:</Text> {business.name}</Text>
          <Text size="sm"><Text span fw={600}>Category:</Text> {business.category ?? 'Uncategorized'}</Text>
          <Text size="sm"><Text span fw={600}>Region:</Text> {business.regionName}</Text>
          <Text size="sm"><Text span fw={600}>Mentions:</Text> {business.mentionCount}</Text>
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" leftSection={<IconX size={16} />} onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            color="orange"
            leftSection={<IconPlayerPause size={16} />}
            onClick={handleDeactivate}
            loading={deactivateBusinessMutation.isPending}
            disabled={deactivateBusinessMutation.isPending}
          >
            Deactivate Business
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
